import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Tooltip, FormControlLabel, Checkbox,
} from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useCatalogService } from '../services/catalogService';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import type { PipelineCredentialResponse } from '../services/catalogService';

interface AllPipelineCredentialsViewProps {
  onCountChange?: (count: number) => void;
}

/**
 * Every pipeline credential across all services, keyed by client_id — including
 * credentials whose owning offering was deleted (which the per-offering view can
 * never reach). Credentials authorize per client_id + OAuth scope, not per
 * service_id, so an orphaned owner does not stop them working.
 */
const AllPipelineCredentialsView: React.FC<AllPipelineCredentialsViewProps> = ({ onCountChange }) => {
  const catalogService = useCatalogService();
  const [credentials, setCredentials] = useState<PipelineCredentialResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<PipelineCredentialResponse | null>(null);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await catalogService.listAllPipelineCredentials();
      setCredentials(result.credentials);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load pipeline credentials'));
    } finally {
      setLoading(false);
    }
  }, [catalogService]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onCountChange?.(credentials.length); }, [credentials.length, onCountChange]);

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    try {
      await catalogService.revokePipelineCredential(revokeTarget.service_id, revokeTarget.client_id);
      setRevokeTarget(null);
      showSnackbar('Credential revoked');
      load();
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to revoke credential'), 'error');
    }
  }, [revokeTarget, catalogService, showSnackbar, load]);

  const visible = showRevoked ? credentials : credentials.filter(c => c.status === 'active');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, width: '100%' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 3 }}>
      {error && <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</InlineAlert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">All pipeline credentials</Typography>
        {credentials.some(c => c.status === 'revoked') && (
          <FormControlLabel
            control={<Checkbox size="small" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />}
            label={<Typography variant="body2">Show revoked</Typography>}
          />
        )}
      </Box>

      {visible.length === 0 ? (
        <Typography color="text.secondary">
          {credentials.length === 0 ? 'No pipeline credentials exist yet.' : 'No active credentials. Check "Show revoked" to see revoked ones.'}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client ID</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Owning service</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((cred) => (
                <TableRow key={`${cred.service_id}:${cred.client_id}`}>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{cred.client_id}</Typography></TableCell>
                  <TableCell>{cred.label}</TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{cred.service_id}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{new Date(cred.created_at).toLocaleString()}</Typography></TableCell>
                  <TableCell><Chip label={cred.status} size="small" color={cred.status === 'active' ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">
                    {cred.status === 'active' && (
                      <Tooltip title="Revoke credential">
                        <IconButton size="small" color="error" onClick={() => setRevokeTarget(cred)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={!!revokeTarget}
        title="Revoke Credential?"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        confirmText="Revoke"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Revoke credential <strong>{revokeTarget?.label}</strong> (owning service <code>{revokeTarget?.service_id}</code>)?
        </Typography>
        <InlineAlert severity="warning">
          Pipelines using this credential will lose access immediately. This action cannot be undone.
        </InlineAlert>
      </DeleteConfirmDialog>

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
};

export default AllPipelineCredentialsView;
